import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { graphql } from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import type { ThirdPartyForm_ThirdParty } from '../../fragments/third-party.fragment';

import { useIsBankInfoRequired } from './hooks/useIsBankInfoRequired.hook';

export const ThirdPartyForm_BankInfoStep_ThirdParty_Fragment = graphql(`
  fragment ThirdPartyForm_BankInfoStep_ThirdParty_Fragment on ThirdParty {
    bankInfo {
      _id
      accountOwner
      domiciliation
      bankCode
      branchCode
      accountNumber
      certificateKey
      iban
      swift
    }
  }
`);

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
export const ThirdPartyBankInfoFormStep = ({}: Readonly<Props>) => {
  const { errors, handleBlur, handleChange, touched, values } =
    useFormikContext<ThirdPartyForm_ThirdParty>();

  const { isRequired } = useIsBankInfoRequired();

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.accountOwner') &&
              getIn(touched, 'bankInfo.accountOwner')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'bankInfo.accountOwner')}
          id="third-party-bank-info-account-owner"
          label="Titulaire du compte"
          name="bankInfo.accountOwner"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.accountOwner}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.bankCode') &&
              getIn(touched, 'bankInfo.bankCode')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'bankInfo.bankCode')}
          id="third-party-bank-info-bank-code"
          label="Code banque"
          name="bankInfo.bankCode"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.bankCode}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.branchCode') &&
              getIn(touched, 'bankInfo.branchCode')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'bankInfo.branchCode')}
          id="third-party-bank-info-branch-code"
          label="Code guichet"
          name="bankInfo.branchCode"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.branchCode}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.accountNumber') &&
              getIn(touched, 'bankInfo.accountNumber')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'bankInfo.accountNumber',
          )}
          id="third-party-bank-info-account-number"
          label="Numéro de compte"
          name="bankInfo.accountNumber"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.accountNumber}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.certificateKey') &&
              getIn(touched, 'bankInfo.certificateKey')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'bankInfo.certificateKey',
          )}
          id="third-party-bank-info-certificate-key"
          label="Clef RIB"
          name="bankInfo.certificateKey"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.certificateKey}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.iban') && getIn(touched, 'bankInfo.iban')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'bankInfo.iban')}
          id="third-party-bank-info-iban"
          label="IBAN"
          name="bankInfo.iban"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.iban}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.swift') &&
              getIn(touched, 'bankInfo.swift')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'bankInfo.swift')}
          id="third-party-bank-info-swift"
          label="SWIFT"
          name="bankInfo.swift"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.swift}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'bankInfo.domiciliation') &&
              getIn(touched, 'bankInfo.domiciliation')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'bankInfo.domiciliation',
          )}
          id="third-party-bank-info-domiciliation"
          label="Domiciliation"
          name="bankInfo.domiciliation"
          onBlur={handleBlur}
          onChange={handleChange}
          required={isRequired}
          type="text"
          value={values.bankInfo?.domiciliation}
          variant="outlined"
        />
      </Grid>
    </Grid>
  );
};
